import { clearPurchaseControls } from '@theme/tbwl-product-state';
import { StandardEvents } from '@shopify/events';

// Preserve the existing service's last fitting priority; never trust its ID
// without membership and current availability in this Shopify product.
export function organizerVariant(brands, brand, model, size, variants) {
  const choices = brands?.[brand]?.[model]?.[size];
  if (!choices || !Object.keys(choices).length) return null;
  const mapping = choices[Object.keys(choices).pop()];
  const id = String(mapping?.variant_id || mapping?.variant || '');
  return variants.find(v => String(v.id) === id && v.available === true) || null;
}

class TbwlOrganizer extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.scope = this.closest('.shopify-section');
    this.form = this.scope.querySelector('product-form-component');
    this.picker = this.scope.querySelector('variant-picker');
    this.variants = JSON.parse(this.querySelector('[data-organizer-variants]').textContent);
    this.selects = [...this.querySelectorAll('select')];
    this.status = this.querySelector('[role="status"]');
    this.abort = new AbortController();
    this.scope.addEventListener(StandardEvents.productSelect, async event => {
      try {
        const {detail} = await event.promise;
        if (this.form.dataset.organizerVariant && String(detail?.resource?.id) === this.form.dataset.organizerVariant && detail.resource.available) {
          this.dataset.matchedState = 'true';
          this.status.textContent = `${this.dataset.matched}: ${this.selects.map(s=>s.value).join(' / ')}`;
        }
        else if (!detail?.resource?.available) this.status.textContent = this.dataset.unavailable;
      } catch { this.status.textContent = this.dataset.error; }
    }, {signal:this.abort.signal});
    this.invalidate();
    this.addEventListener('change', event => this.select(event), {signal:this.abort.signal});
    this.querySelector('[data-retry]').addEventListener('click', () => this.load(), {signal:this.abort.signal});
    this.load();
  }
  disconnectedCallback() { this.abort?.abort(); this.loadGeneration = (this.loadGeneration || 0) + 1; this.bound = false; }
  updateProgress() {
    const count = this.selects.filter(s => s.value).length;
    const progress = this.querySelector('progress');
    if (progress) progress.value = count;
    const label = this.querySelector('[data-progress-count]');
    if (label) label.textContent = `${count} / 3`;
  }
  invalidate() {
    this.dataset.matchedState = 'false';
    if (!this.form) return;
    this.form.dataset.organizerVariant = '';
    clearPurchaseControls(this.form);
    const id = this.form.querySelector('input[name="id"]');
    if (id) id.disabled = true;
  }
  async load() {
    const generation = this.loadGeneration = (this.loadGeneration || 0) + 1;
    this.invalidate();
    this.options(0, []); this.options(1, []); this.options(2, []);
    this.setLoadState('loading');
    try {
      const response = await fetch('https://complements.swistart.work/api/brands', {signal: AbortSignal.timeout(15000)});
      if (!response.ok) throw Error('mapping unavailable');
      const data = await response.json();
      if (generation !== this.loadGeneration) return;
      if (!data.brands || typeof data.brands !== 'object' || Array.isArray(data.brands)) throw Error('invalid mapping');
      this.brands = data.brands;
      this.options(0, Object.keys(this.brands));
      this.options(1, []); this.options(2, []);
      this.setLoadState(Object.keys(this.brands).length ? 'success' : 'empty');
    } catch { if (generation === this.loadGeneration) this.setLoadState('failure'); }
  }
  setLoadState(state) {
    this.dataset.state = state;
    this.querySelector('[data-retry]').hidden = state !== 'failure';
    this.status.textContent = state === 'loading' ? this.dataset.loading : state === 'failure' ? this.dataset.error : state === 'empty' ? this.dataset.empty : '';
  }
  options(index, values) {
    const select = this.selects[index];
    select.replaceChildren(new Option(select.dataset.placeholder, ''));
    for (const value of values.sort()) select.add(new Option(value, value));
    select.disabled = !values.length;
    this.updateProgress();
  }
  select(event) {
    this.invalidate();
    this.status.textContent = '';
    const index = this.selects.indexOf(event.target);
    if (index < 0) return;
    this.updateProgress();
    const [brand, model, size] = this.selects.map(s => s.value);
    if (index === 0) { this.options(1, Object.keys(this.brands?.[brand] || {})); this.options(2, []); return; }
    if (index === 1) { this.options(2, Object.keys(this.brands?.[brand]?.[model] || {})); return; }
    const variant = organizerVariant(this.brands, brand, model, size, this.variants);
    if (!variant || !this.picker?.fetchUpdatedSection) { this.status.textContent = this.dataset.unavailable; return; }
    this.form.dataset.organizerVariant = String(variant.id);
    // The native section response owns price, quantity rules and buy controls.
    // Its existing generation guard rejects stale responses and failures stay closed.
    this.picker.fetchUpdatedSection(`${this.dataset.url}?variant=${encodeURIComponent(variant.id)}`, {detail:{variantId:String(variant.id)}});
  }
}
if (!customElements.get('tbwl-organizer')) customElements.define('tbwl-organizer', TbwlOrganizer);
